import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  User,
  Users,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Shield,
  UserCheck,
  UserX,
  X,
  Crown,
  Filter,
  Hash,
  Badge
} from 'lucide-react';
import { userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const EmployeeManagement = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [showPermanentDeleteErrorModal, setShowPermanentDeleteErrorModal] = useState(false);
  const [permanentDeleteError, setPermanentDeleteError] = useState(null);
  const [employeeForPermanentDelete, setEmployeeForPermanentDelete] = useState(null);
  const [showPermanentDeleteConfirmModal, setShowPermanentDeleteConfirmModal] = useState(false);
  const [employeeToPermanentDelete, setEmployeeToPermanentDelete] = useState(null);
  const [showReactivateConfirmModal, setShowReactivateConfirmModal] = useState(false);
  const [employeeToReactivate, setEmployeeToReactivate] = useState(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      username: '',
      password: '',
      role: 'EMPLOYEE',
      employeeCode: ''
    }
  });

  const { data: employees, isLoading } = useQuery(
    'all-employees',
    () => {
      console.log('EmployeeManagement - User role:', user?.role);
      if (user?.role === 'SUPER_ADMIN') {
        console.log('EmployeeManagement - Calling getAllEmployees (Super Admin)');
        return userAPI.getAllEmployees();
      } else {
        console.log('EmployeeManagement - Calling getManagersAndEmployeesAdmin (Admin)');
        return userAPI.getManagersAndEmployeesAdmin();
      }
    },
    {
      enabled: !!user, // Only run when user is loaded
      select: (response) => {
        console.log('EmployeeManagement - API Response:', response);
        // Handle different response structures
        if (response?.data?.data) {
          return response.data.data; // For admin endpoint with {data: [...], total: ...}
        } else if (response?.data) {
          return response.data; // For super admin endpoint with direct array
        } else {
          return []; // Fallback to empty array
        }
      },
      onError: (error) => {
        console.error('EmployeeManagement - Error fetching employees:', error);
        toast.error('Failed to fetch employees: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const createMutation = useMutation(
    (userData) => {
      // Create Manager or Employee based on role
      if (userData.role === 'MANAGER') {
        return userAPI.createManager(userData);
      } else {
        return userAPI.createEmployee(userData);
      }
    },
    {
      onSuccess: (response, variables) => {
        const roleName = variables.role === 'MANAGER' ? 'Manager' : 'Employee';
        toast.success(`${roleName} created successfully`);
        queryClient.invalidateQueries('all-employees');
        setShowCreateModal(false);
        reset();
      },
      onError: (error, variables) => {
        const roleName = variables.role === 'MANAGER' ? 'manager' : 'employee';
        console.error('Error creating user:', error);
        console.error('Error response:', error.response);
        console.error('Error response data:', error.response?.data);
        
        let errorMessage = `Failed to create ${roleName}`;
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        toast.error(errorMessage);
      },
    }
  );

  const editMutation = useMutation(
    ({ userId, userData }) => {
      if (user?.role === 'ADMIN') {
        return userAPI.updateUserAdmin(userId, userData);
      } else {
        return userAPI.updateUser(userId, userData); // Super Admin endpoint
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('all-employees');
        toast.success('User updated successfully');
        setShowEditModal(false);
        setSelectedEmployee(null);
      },
      onError: (error) => {
        console.error('Error updating user:', error);
        const errorMessage = error.response?.data?.message ||
                           error.response?.data ||
                           error.message ||
                           'Failed to update user';
        toast.error(errorMessage);
      },
    }
  );

  const deleteMutation = useMutation(
    (employeeId) => {
      console.log('EmployeeManagement - Deleting employee with role:', user?.role);
      if (user?.role === 'SUPER_ADMIN') {
        console.log('EmployeeManagement - Calling deleteUser (Super Admin)');
        return userAPI.deleteUser(employeeId);
      } else {
        console.log('EmployeeManagement - Calling deleteEmployee (Admin)');
        return userAPI.deleteEmployee(employeeId);
      }
    },
    {
      onSuccess: () => {
        toast.success('Employee deactivated successfully');
        queryClient.invalidateQueries('all-employees');
      },
      onError: (error) => {
        console.error('EmployeeManagement - Delete error:', error);
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           error.message || 
                           'Failed to deactivate employee';
        toast.error(errorMessage);
      },
    }
  );

  const reactivateMutation = useMutation(
    (employeeId) => {
      console.log('EmployeeManagement - Reactivating employee with role:', user?.role);
      if (user?.role === 'SUPER_ADMIN') {
        console.log('EmployeeManagement - Calling reactivateUser (Super Admin)');
        return userAPI.reactivateUser(employeeId);
      } else {
        console.log('EmployeeManagement - Calling reactivateEmployee (Admin)');
        return userAPI.reactivateEmployee(employeeId);
      }
    },
    {
      onSuccess: () => {
        toast.success('Employee reactivated successfully');
        queryClient.invalidateQueries('all-employees');
      },
      onError: (error) => {
        console.error('EmployeeManagement - Reactivate error:', error);
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           error.message || 
                           'Failed to reactivate employee';
        toast.error(errorMessage);
      },
    }
  );

  const permanentDeleteMutation = useMutation(
    (employeeId) => {
      console.log('EmployeeManagement - Permanently deleting employee with role:', user?.role);
      if (user?.role === 'SUPER_ADMIN') {
        console.log('EmployeeManagement - Calling permanentlyDeleteUser (Super Admin)');
        return userAPI.permanentlyDeleteUser(employeeId);
      } else {
        console.log('EmployeeManagement - Calling permanentlyDeleteEmployee (Admin)');
        return userAPI.permanentlyDeleteEmployee(employeeId);
      }
    },
    {
      onSuccess: () => {
        toast.success('Employee permanently deleted successfully');
        queryClient.invalidateQueries('all-employees');
        setShowPermanentDeleteErrorModal(false);
        setPermanentDeleteError(null);
        setEmployeeForPermanentDelete(null);
      },
      onError: (error) => {
        console.error('EmployeeManagement - Permanent delete error:', error);
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           error.message || 
                           'Failed to permanently delete employee';
        
        // Check if error is due to existing timesheets or project assignments
        if (errorMessage.includes('timesheets') || errorMessage.includes('projects') || errorMessage.includes('assigned')) {
          setPermanentDeleteError(errorMessage);
          setShowPermanentDeleteErrorModal(true);
        } else {
          toast.error(errorMessage);
        }
      },
    }
  );

  // Helper functions for role display (similar to Super Admin)
  const getRoleIcon = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'ADMIN':
        return <Shield className="w-4 h-4 text-blue-500" />;
      case 'MANAGER':
        return <Shield className="w-4 h-4 text-purple-500" />;
      case 'EMPLOYEE':
        return <User className="w-4 h-4 text-green-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'bg-yellow-100 text-yellow-800';
      case 'ADMIN':
        return 'bg-blue-100 text-blue-800';
      case 'MANAGER':
        return 'bg-purple-100 text-purple-800';
      case 'EMPLOYEE':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleName = (role) => (typeof role === 'object' && role !== null ? role.name : role);

  const filteredEmployees = (Array.isArray(employees) ? employees : []).filter(employee => {
    const matchesSearch = 
      employee.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.department?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Client-side role filtering
    const matchesRole = roleFilter === '' || getRoleName(employee.role) === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  const handleCreateUser = (data) => {
    createMutation.mutate(data);
  };

  const handleDelete = async (employeeId) => {
    console.log('EmployeeManagement - Attempting to delete employee ID:', employeeId);
    console.log('EmployeeManagement - Current user role:', user?.role);
    
    setIsLoadingDetails(true);
    setEmployeeToDelete(employeeId);
    
    try {
      // Fetch employee details to check for timesheets and projects
      console.log('EmployeeManagement - Calling API for employee details...');
      const response = user?.role === 'SUPER_ADMIN' 
        ? await userAPI.getUserDetails(employeeId)
        : await userAPI.getEmployeeDetails(employeeId);
      
      console.log('EmployeeManagement - API Response:', response);
      setEmployeeDetails(response.data);
      setShowDeleteModal(true);
    } catch (error) {
      console.error('EmployeeManagement - Error fetching employee details:', error);
      console.error('EmployeeManagement - Error response:', error.response);
      console.error('EmployeeManagement - Error status:', error.response?.status);
      console.error('EmployeeManagement - Error data:', error.response?.data);
      toast.error('Failed to fetch employee details: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const confirmDelete = () => {
    if (employeeToDelete) {
      deleteMutation.mutate(employeeToDelete);
      setShowDeleteModal(false);
      setEmployeeToDelete(null);
      setEmployeeDetails(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setEmployeeToDelete(null);
    setEmployeeDetails(null);
  };

  const handleViewDetails = (employee) => {
    setSelectedEmployee(employee);
    setShowDetails(true);
  };

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    // Pre-populate the form with current employee data
    reset({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      email: employee.email || '',
      username: employee.username || '',
      role: employee.role || 'EMPLOYEE',
      employeeCode: employee.employeeCode || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = (data) => {
    if (selectedEmployee) {
      editMutation.mutate({
        userId: selectedEmployee.id,
        userData: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          username: data.username,
          employeeCode: data.employeeCode
        }
      });
    }
  };

  const handleReactivate = (employeeId, employeeName) => {
    setEmployeeToReactivate({ id: employeeId, name: employeeName });
    setShowReactivateConfirmModal(true);
  };

  const confirmReactivate = () => {
    if (employeeToReactivate) {
      reactivateMutation.mutate(employeeToReactivate.id);
      setShowReactivateConfirmModal(false);
      setEmployeeToReactivate(null);
    }
  };

  const cancelReactivate = () => {
    setShowReactivateConfirmModal(false);
    setEmployeeToReactivate(null);
  };

  const handlePermanentDelete = (employeeId, employeeName) => {
    setEmployeeToPermanentDelete({ id: employeeId, name: employeeName });
    setShowPermanentDeleteConfirmModal(true);
  };

  const confirmPermanentDelete = () => {
    if (employeeToPermanentDelete) {
      setEmployeeForPermanentDelete(employeeToPermanentDelete);
      permanentDeleteMutation.mutate(employeeToPermanentDelete.id);
      setShowPermanentDeleteConfirmModal(false);
      setEmployeeToPermanentDelete(null);
    }
  };

  const cancelPermanentDelete = () => {
    setShowPermanentDeleteConfirmModal(false);
    setEmployeeToPermanentDelete(null);
  };

  const handleDeactivateFromErrorModal = () => {
    if (employeeForPermanentDelete) {
      setShowPermanentDeleteErrorModal(false);
      setPermanentDeleteError(null);
      // Trigger deactivation instead
      handleDelete(employeeForPermanentDelete.id);
      setEmployeeForPermanentDelete(null);
    }
  };

  const closePermanentDeleteErrorModal = () => {
    setShowPermanentDeleteErrorModal(false);
    setPermanentDeleteError(null);
    setEmployeeForPermanentDelete(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-primary-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {user?.role === 'ADMIN' ? 'Manager & Employee Management' : 'User Management'}
              </h1>
              <p className="text-gray-600 mt-1">
                {user?.role === 'ADMIN' 
                  ? 'Manage manager and employee accounts' 
                  : 'Manage employee and manager accounts'
                }
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{user?.role === 'ADMIN' ? 'Add Manager/Employee' : 'Add User'}</span>
          </button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, email, or department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Roles</option>
              <option value="MANAGER">Manager</option>
              <option value="EMPLOYEE">Employee</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Employees List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Employees ({filteredEmployees.length})
          </h2>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredEmployees.map((employee, index) => (
            <motion.div
              key={employee.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="px-6 py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 rounded-full bg-primary-500 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">
                      {employee.firstName} {employee.lastName}
                    </h3>
                    <p className="text-sm text-gray-500">{employee.email}</p>
                    <p className="text-sm text-gray-500">@{employee.username}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        employee.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {employee.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    {getRoleIcon(getRoleName(employee.role))}
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(getRoleName(employee.role))}`}>
                      {getRoleName(employee.role)}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewDetails(employee)}
                      className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(employee)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Edit Employee"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    {getRoleName(employee.role) !== 'SUPER_ADMIN' && (
                      <>
                        {employee.isActive ? (
                          <button
                            onClick={() => handleDelete(employee.id)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            title="Deactivate Employee"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => handleReactivate(employee.id, `${employee.firstName} ${employee.lastName}`)}
                              disabled={reactivateMutation.isLoading}
                              className="p-2 text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50"
                              title="Reactivate Employee"
                            >
                              <UserCheck className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handlePermanentDelete(employee.id, `${employee.firstName} ${employee.lastName}`)}
                              disabled={permanentDeleteMutation.isLoading}
                              className="p-2 text-gray-400 hover:text-red-700 transition-colors disabled:opacity-50"
                              title="Permanently Delete Employee"
                            >
                              <UserX className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {filteredEmployees.length === 0 && (
          <div className="px-6 py-8 text-center">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No employees found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || roleFilter
                ? 'Try adjusting your search or filter criteria.'
                : 'No employees have been added yet.'
              }
            </p>
          </div>
        )}
      </motion.div>

      {/* Employee Details Modal */}
      {showDetails && selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Employee Details</h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-6">
                <div className="h-16 w-16 rounded-full bg-primary-500 flex items-center justify-center">
                  <span className="text-lg font-medium text-white">
                    {selectedEmployee.firstName?.charAt(0)}{selectedEmployee.lastName?.charAt(0)}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedEmployee.firstName} {selectedEmployee.lastName}
                  </h2>
                  <p className="text-gray-600">{selectedEmployee.email}</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${
                    selectedEmployee.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedEmployee.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Personal Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{selectedEmployee.email}</span>
                    </div>
                    {selectedEmployee.username && (
                      <div className="flex items-center space-x-3">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">@{selectedEmployee.username}</span>
                      </div>
                    )}
                    {selectedEmployee.phone && (
                      <div className="flex items-center space-x-3">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{selectedEmployee.phone}</span>
                      </div>
                    )}
                    {selectedEmployee.address && (
                      <div className="flex items-center space-x-3">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{selectedEmployee.address}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Work Information</h4>
                  <div className="space-y-3">
                    {selectedEmployee.employeeCode && (
                      <div className="flex items-center space-x-3">
                        <Hash className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Employee No: <span className="font-medium">{selectedEmployee.employeeCode}</span>
                        </span>
                      </div>
                    )}
                    <div className="flex items-center space-x-3">
                      <Shield className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {selectedEmployee.department || 'No Department'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {selectedEmployee.position || 'No Position'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Joined: {new Date(selectedEmployee.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDetails(false);
                    handleEdit(selectedEmployee);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                >
                  <Edit className="w-4 h-4" />
                  <span>Edit Employee</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                {user?.role === 'ADMIN' ? 'Create New Manager/Employee' : 'Create New User'}
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmit(handleCreateUser)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      {...register('firstName', { required: 'First name is required' })}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter first name"
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      {...register('lastName', { required: 'Last name is required' })}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter last name"
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee Code</label>
                  <input
                    {...register('employeeCode')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Optional employee code / number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter email address"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                    <input
                      {...register('username', { required: 'Username is required' })}
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter username"
                    />
                    {errors.username && (
                      <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                    <input
                      {...register('password', { 
                        required: 'Password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters'
                        }
                      })}
                      type="password"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter password"
                    />
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <select
                    {...register('role', { required: 'Role is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="EMPLOYEE">Employee</option>
                    <option value="MANAGER">Manager</option>
                  </select>
                  {errors.role && (
                    <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
                  )}
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      reset();
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isLoading}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {createMutation.isLoading ? 'Creating...' : (user?.role === 'ADMIN' ? 'Create Manager/Employee' : 'Create User')}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Deactivate Employee</h3>
              <button
                onClick={cancelDelete}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              {isLoadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="spinner"></div>
                  <span className="ml-2 text-gray-600">Loading employee details...</span>
                </div>
              ) : employeeDetails ? (
                <div className="space-y-6">
                  {/* Employee Info */}
                  <div className="flex items-center space-x-4">
                    <div className="h-16 w-16 rounded-full bg-primary-500 flex items-center justify-center">
                      <span className="text-lg font-medium text-white">
                        {employeeDetails.employee?.firstName?.charAt(0) || employeeDetails.user?.firstName?.charAt(0)}
                        {employeeDetails.employee?.lastName?.charAt(0) || employeeDetails.user?.lastName?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {employeeDetails.employee?.firstName || employeeDetails.user?.firstName} {employeeDetails.employee?.lastName || employeeDetails.user?.lastName}
                      </h2>
                      <p className="text-gray-600">{employeeDetails.employee?.email || employeeDetails.user?.email}</p>
                    </div>
                  </div>

                  {/* Information about related data */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="w-5 h-5 text-blue-400">ℹ️</div>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          Related Data Information
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <ul className="list-disc list-inside space-y-1">
                            {employeeDetails.hasTimesheets && (
                              <li>This employee has existing timesheets (will be preserved)</li>
                            )}
                            {employeeDetails.projectCount > 0 && (
                              <li>Active project assignments ({employeeDetails.projectCount} project{employeeDetails.projectCount !== 1 ? 's' : ''})</li>
                            )}
                            {!employeeDetails.hasTimesheets && employeeDetails.projectCount === 0 && (
                              <li>No related data found</li>
                            )}
                          </ul>
                          <p className="mt-2 font-medium">
                            Note: All timesheet data will be preserved for historical records.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Warning message */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="w-5 h-5 text-yellow-400">⚠️</div>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Confirm Deactivation
                        </h3>
                        <p className="mt-1 text-sm text-yellow-700">
                          Are you sure you want to deactivate this employee? They will no longer be able to log in to the system, but all their data will be preserved.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Project Details */}
                  {employeeDetails.projectCount > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Assigned Projects:</h4>
                      <div className="space-y-2">
                        {employeeDetails.projects?.map((project, index) => (
                          <div key={project.id || index} className="flex items-center justify-between bg-white rounded border p-3">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{project.name}</p>
                              <p className="text-xs text-gray-500">{project.description || 'No description'}</p>
                            </div>
                            <span className="text-xs text-gray-500">
                              {project.status || 'Active'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={cancelDelete}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDelete}
                      disabled={deleteMutation.isLoading}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {deleteMutation.isLoading ? 'Deactivating...' : 'Deactivate Employee'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Failed to load employee details</p>
                  <button
                    onClick={cancelDelete}
                    className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedEmployee && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {user?.role === 'ADMIN' ? 'Edit Manager/Employee' : 'Edit User'}
              </h3>
              <form onSubmit={handleSubmit(handleEditSubmit)} className="space-y-4">
                <div>
                  <label className="form-label">Username</label>
                  <input
                    {...register('username', { required: 'Username is required' })}
                    type="text"
                    className="form-input"
                    required
                  />
                  {errors.username && (
                    <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="form-label">Email</label>
                  <input
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    type="email"
                    className="form-input"
                    required
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="form-label">First Name</label>
                  <input
                    {...register('firstName', { required: 'First name is required' })}
                    type="text"
                    className="form-input"
                    required
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="form-label">Last Name</label>
                  <input
                    {...register('lastName', { required: 'Last name is required' })}
                    type="text"
                    className="form-input"
                    required
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Employee Code</label>
                  <input
                    {...register('employeeCode')}
                    type="text"
                    className="form-input"
                    placeholder="Optional employee code / number"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedEmployee(null);
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editMutation.isLoading}
                    className="btn-primary"
                  >
                    {editMutation.isLoading ? 'Updating...' : 'Update User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Confirmation Modal */}
      {showReactivateConfirmModal && employeeToReactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Confirm Reactivation</h3>
              <button
                onClick={cancelReactivate}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 text-green-400">✓</div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      Reactivate Employee
                    </h3>
                    <p className="mt-1 text-sm text-green-700">
                      Are you sure you want to reactivate <strong>"{employeeToReactivate.name}"</strong>?
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 text-blue-400">ℹ️</div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      What happens when you reactivate?
                    </h3>
                    <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
                      <li>The user will be able to log in to the system again</li>
                      <li>All existing data (timesheets, projects) will remain intact</li>
                      <li>The user's account status will change to "Active"</li>
                      <li>All previous permissions and assignments will be restored</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={cancelReactivate}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmReactivate}
                  disabled={reactivateMutation.isLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {reactivateMutation.isLoading ? 'Reactivating...' : 'Yes, Reactivate'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {showPermanentDeleteConfirmModal && employeeToPermanentDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Confirm Permanent Deletion</h3>
              <button
                onClick={cancelPermanentDelete}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 text-red-400">⚠️</div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Warning: Permanent Deletion
                    </h3>
                    <p className="mt-1 text-sm text-red-700">
                      Are you sure you want to <strong>PERMANENTLY DELETE</strong> "{employeeToPermanentDelete.name}"?
                    </p>
                    <p className="mt-2 text-sm font-semibold text-red-800">
                      This action cannot be undone!
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 text-yellow-400">ℹ️</div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      What happens when you permanently delete?
                    </h3>
                    <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside space-y-1">
                      <li>The user account will be completely removed from the system</li>
                      <li>All user data will be permanently deleted</li>
                      <li>This action cannot be reversed</li>
                      <li>If the user has timesheets or project assignments, this operation may fail</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 text-blue-400">💡</div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Consider Deactivation Instead
                    </h3>
                    <p className="mt-1 text-sm text-blue-700">
                      If you want to prevent login while preserving data, consider deactivating the user instead. This preserves all historical records and allows reactivation later.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={cancelPermanentDelete}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmPermanentDelete}
                  disabled={permanentDeleteMutation.isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {permanentDeleteMutation.isLoading ? 'Deleting...' : 'Yes, Delete Permanently'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Permanent Delete Error Modal */}
      {showPermanentDeleteErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Cannot Permanently Delete</h3>
              <button
                onClick={closePermanentDeleteErrorModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 text-red-400">⚠️</div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Permanent Deletion Not Allowed
                    </h3>
                    <p className="mt-1 text-sm text-red-700">
                      {permanentDeleteError || 'This user cannot be permanently deleted at this time.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 text-blue-400">ℹ️</div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Why can't I permanently delete?
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <ul className="list-disc list-inside space-y-1">
                        {permanentDeleteError?.includes('timesheets') && (
                          <li>This user has existing timesheets that must be preserved</li>
                        )}
                        {permanentDeleteError?.includes('projects') && (
                          <li>This user is assigned to active projects</li>
                        )}
                        {permanentDeleteError?.includes('assigned') && (
                          <li>This user has active project assignments</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 text-yellow-400">💡</div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Recommended Action
                    </h3>
                    <p className="mt-1 text-sm text-yellow-700">
                      Instead of permanently deleting, you can deactivate this user. Deactivation will:
                    </p>
                    <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside space-y-1">
                      <li>Prevent the user from logging in</li>
                      <li>Preserve all historical data (timesheets, projects)</li>
                      <li>Allow reactivation in the future if needed</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={closePermanentDeleteErrorModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeactivateFromErrorModal}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Deactivate Instead
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;
